import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { ReactNode } from 'react'
import { Fragment, useState } from 'react'
import { Link } from 'react-router-dom'
import { ArrowUpRight, ChevronRight, CircleAlert, Inbox, MessageSquareText, Search, ShieldCheck, Undo2 } from 'lucide-react'

import { PageHeader } from '../components/layout'
import {
  applyDepositReversal,
  dismissDepositReversal,
  getDepositReversal,
  getDepositReversals,
  getOverview,
  type AdminDepositReversal,
  type DepositReversalStatus,
} from '../api/admin'
import {
  Avatar,
  Button,
  Callout,
  Empty,
  EmptyState,
  Field,
  IdChip,
  Input,
  KeyValue,
  Modal,
  Pager,
  Panel,
  SkeletonText,
  StatusPill,
  Table,
  TableSkeleton,
  Tabs,
  Textarea,
  Tooltip,
  cx,
  describeError,
  money,
  short,
  tableClass,
  ui,
  useToast,
  useTotp,
  when,
  type Tone,
} from '../components/ui/Ui'
import styles from './DepositReversals.module.css'

type Tab = 'pending_review' | 'applied' | 'dismissed' | 'all'

const TABS: ReadonlyArray<{ value: Tab; label: string }> = [
  { value: 'pending_review', label: 'Ждут решения' },
  { value: 'applied', label: 'Сторнированы' },
  { value: 'dismissed', label: 'Сняты' },
  { value: 'all', label: 'Все' },
]

const TABLE_HEADERS = ['Пользователь · исчезла из сети', 'Актив · сеть', 'Зачислено', 'Хеш', 'Статус', ''] as const

const ACCOUNT_RU: Record<string, string> = { spot: 'спот', futures: 'фьючерсы', copy: 'копирование' }

/** The watcher's fixed wording, in the panel's language; anything else is shown as it came. */
const REASON_RU: Record<string, string> = {
  'transaction is no longer on chain after having been credited': 'транзакция больше не видна в сети после зачисления',
}

function reasonOf(reason: string): string {
  return REASON_RU[reason] ?? reason
}

/** What the operator is looking at: the row, or the row with a decision being confirmed. */
type Mode = 'view' | 'apply' | 'dismiss'

function newIdempotencyKey(): string {
  return typeof crypto !== 'undefined' && 'randomUUID' in crypto ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(16).slice(2)}`
}

/** The network code as a small mono chip (`tron`, `bsc`). */
function Net({ code }: { code: string }): JSX.Element {
  return <span className={styles.net}>{code}</span>
}

/** A transaction hash with a copy button and, when the network has an explorer, an arrow that opens it. */
function TxLink({ hash, url, head = 8, tail = 6, className }: { hash: string; url: string | null; head?: number; tail?: number; className?: string }): JSX.Element {
  return (
    <span className={cx(styles.tx, className)}>
      <IdChip value={hash} head={head} tail={tail} label="хеш" bare className={styles.txChip} />
      {url ? (
        <Tooltip content="Открыть в обозревателе">
          <a href={url} target="_blank" rel="noreferrer" className={styles.explorer} aria-label="Открыть транзакцию в обозревателе" onClick={(event) => event.stopPropagation()}>
            <ArrowUpRight aria-hidden="true" />
          </a>
        </Tooltip>
      ) : null}
    </span>
  )
}

/** When the deposit was credited, as short as it stays unambiguous: the time alone on the day it vanished. */
function creditedShort(row: AdminDepositReversal): string {
  const credited = when(row.creditedAt)
  const vanished = when(row.vanishedAt)
  return credited.slice(0, 10) === vanished.slice(0, 10) ? credited.slice(11) : credited
}

/** The notes a row carries, if any: they get their own line under the row. */
function hasNotes(row: AdminDepositReversal): boolean {
  return (row.status === 'applied' && Number(row.shortfall) > 0) || (row.status !== 'pending_review' && Boolean(row.notes))
}

/** Under the row: what the decision left on record. The watcher's own words wait in the detail. */
function RowNotes({ row }: { row: AdminDepositReversal }): JSX.Element {
  return (
    <div className={styles.notes}>
      {row.status === 'applied' && Number(row.shortfall) > 0 ? (
        <div className={`${styles.note} ${styles.noteError}`}>
          <CircleAlert aria-hidden="true" />
          <span>
            Недостача {money(row.shortfall, 2)} {row.assetSymbol}: пользователь уже потратил часть
          </span>
        </div>
      ) : null}
      {row.status !== 'pending_review' && row.notes ? (
        <div className={styles.note}>
          <MessageSquareText aria-hidden="true" />
          <span>{row.notes}</span>
        </div>
      ) : null}
    </div>
  )
}

/** First seen → credited → last seen → gone (→ reversed) as a horizontal line of dated steps. */
function Timeline({ steps }: { steps: ReadonlyArray<{ label: string; at: string | null; tone?: 'down' | 'warning' }> }): JSX.Element {
  return (
    <ol className={styles.timeline}>
      {steps.map((step) => (
        <li key={step.label} className={cx(styles.step, step.tone === 'down' ? styles.stepDown : step.tone === 'warning' ? styles.stepWarning : '')}>
          <span className={styles.stepDot} aria-hidden="true" />
          <span className={styles.stepLabel}>{step.label}</span>
          <span className={styles.stepAt}>{when(step.at)}</span>
        </li>
      ))}
    </ol>
  )
}

/** A titled block inside the detail dialog. */
function Block({ title, children }: { title: ReactNode; children: ReactNode }): JSX.Element {
  return (
    <section className={styles.block}>
      <p className={ui.sectionTitle}>{title}</p>
      {children}
    </section>
  )
}

/**
 * The review a reorg opens. A watcher noticed that a credited transaction is
 * no longer on chain and wrote it down; nothing moved. A person decides here:
 * «Сторнировать» takes the credit back through the ledger — what is still on
 * the spot sub-account, the rest written down as a shortfall — and
 * «Снять» leaves the credit standing. Both need a reason, the second factor,
 * and land in the audit trail.
 */
export default function DepositReversals(): JSX.Element {
  const [tab, setTab] = useState<Tab>('pending_review')
  const [page, setPage] = useState(1)
  const [search, setSearch] = useState('')
  const [applied, setApplied] = useState('')
  const [openId, setOpenId] = useState<string | null>(null)
  const [mode, setMode] = useState<Mode>('view')
  const [reason, setReason] = useState('')
  const [idempotencyKey, setIdempotencyKey] = useState(newIdempotencyKey)
  const queryClient = useQueryClient()
  const toast = useToast()
  const withTotp = useTotp()

  const status: DepositReversalStatus | undefined = tab === 'all' ? undefined : tab
  const list = useQuery({
    queryKey: ['deposit-reversals', tab, page, applied],
    queryFn: () => getDepositReversals({ status, page, search: applied || undefined }),
    refetchInterval: 15_000,
  })
  const detail = useQuery({
    queryKey: ['deposit-reversal', openId],
    queryFn: () => getDepositReversal(openId as string),
    enabled: openId !== null,
  })
  // The sidebar's query (one cache entry): the «Ждут решения» tab shows how many reviews wait.
  const overview = useQuery({ queryKey: ['overview'], queryFn: getOverview, refetchInterval: 20_000, retry: false })
  const waiting = overview.data?.pendingDepositReversals
  const tabs = TABS.map((option) =>
    option.value === 'pending_review' && waiting !== undefined ? { ...option, count: waiting, tone: waiting > 0 ? ('warning' as const) : undefined } : option,
  )

  const refresh = (): void => {
    void queryClient.invalidateQueries({ queryKey: ['deposit-reversals'] })
    void queryClient.invalidateQueries({ queryKey: ['deposit-reversal'] })
    void queryClient.invalidateQueries({ queryKey: ['overview'] })
  }

  const close = (): void => {
    setOpenId(null)
    setMode('view')
    setReason('')
  }

  const open = (row: AdminDepositReversal, next: Mode = 'view'): void => {
    setReason('')
    // One key per decision dialog: a retried click after a lost response replays, never debits twice.
    setIdempotencyKey(newIdempotencyKey())
    setOpenId(row.id)
    setMode(next)
  }

  const decide = useMutation({
    mutationFn: ({ kind, id, code }: { kind: 'apply' | 'dismiss'; id: string; code: string }) =>
      kind === 'apply' ? applyDepositReversal(id, reason.trim(), code, idempotencyKey) : dismissDepositReversal(id, reason.trim(), code, idempotencyKey),
    onSuccess: (result, variables) => {
      if (variables.kind === 'apply') {
        toast(
          Number(result.shortfall) > 0
            ? `Сторнировано ${money(result.reversedAmount)} ${result.reversal.assetSymbol}; недостача ${money(result.shortfall)} ${result.reversal.assetSymbol} записана на сторнировании и в аудите`
            : `Сторнировано ${money(result.reversedAmount)} ${result.reversal.assetSymbol}`,
        )
      } else {
        toast('Пересмотр снят: депозит остаётся зачисленным')
      }
      close()
      refresh()
    },
    onError: (error) => {
      toast(describeError(error), true)
      refresh()
    },
  })

  const run = (kind: 'apply' | 'dismiss', id: string): void => {
    void withTotp((code) => decide.mutateAsync({ kind, id, code }))
  }

  const find = (): void => {
    setApplied(search.trim())
    setPage(1)
  }

  const rows = list.data?.items ?? []
  const r = detail.data?.reversal ?? null
  const projection = detail.data?.projection ?? null
  const openWithdrawals = detail.data?.openWithdrawals ?? null
  const canDecide = r !== null && r.status === 'pending_review'
  const title = r === null ? 'Сторнирование депозита' : mode === 'apply' ? 'Сторнировать депозит' : mode === 'dismiss' ? 'Снять пересмотр' : `${money(r.creditedAmount)} ${r.assetSymbol} · ${r.networkCode}`
  const modalTone: Tone = mode === 'apply' ? 'down' : mode === 'dismiss' ? 'accent' : r?.status === 'pending_review' ? 'warning' : 'neutral'
  const emptyHint =
    'Сюда попадают зачисленные депозиты, транзакции которых исчезли из сети — после реорганизации блокчейна или замены транзакции. Баланс пользователя при этом не меняется, пока оператор не сторнирует зачисление или не снимет пересмотр.'

  return (
    <>
      <PageHeader
        title="Сторнирования депозитов"
        description="Зачисленные депозиты, чьи транзакции исчезли из сети."
      />

      <Panel className={styles.desk}>
        <div className={styles.head}>
          <Tabs
            value={tab}
            options={tabs}
            onChange={(next) => {
              setTab(next)
              setPage(1)
            }}
            variant="underline"
            ariaLabel="Статус пересмотра"
            className={styles.tabs}
          />
          <form
            className={styles.search}
            role="search"
            onSubmit={(event) => {
              event.preventDefault()
              find()
            }}
          >
            <Input compact leading={<Search />} className={styles.searchInput} placeholder="Почта, хеш, адрес или id" aria-label="Поиск по сторнированиям" value={search} onChange={(event) => setSearch(event.target.value)} />
            <Button size="sm" type="submit">
              Найти
            </Button>
          </form>
        </div>

        {applied ? (
          <div className={styles.filter}>
            Найдено по «<span className={styles.filterText}>{applied}</span>»
            <button
              type="button"
              className={styles.filterReset}
              onClick={() => {
                setSearch('')
                setApplied('')
                setPage(1)
              }}
            >
              Сбросить
            </button>
          </div>
        ) : null}

        {list.isError ? (
          <EmptyState compact tone="error" icon={<CircleAlert />} title="Список не загрузился" description={describeError(list.error)} />
        ) : rows.length === 0 && list.isPending ? (
          <TableSkeleton rows={4} headers={TABLE_HEADERS} />
        ) : rows.length === 0 ? (
          tab === 'pending_review' && !applied ? (
            <EmptyState tone="success" icon={<ShieldCheck />} title="Решений не ждёт ни один депозит" description={emptyHint} />
          ) : (
            <EmptyState icon={<Inbox />} title="Записей нет" description={emptyHint} />
          )
        ) : (
          <Table ariaLabel="Сторнирования депозитов">
            <thead>
              <tr>
                <th>{TABLE_HEADERS[0]}</th>
                <th>{TABLE_HEADERS[1]}</th>
                <th className={tableClass.right}>{TABLE_HEADERS[2]}</th>
                <th>{TABLE_HEADERS[3]}</th>
                <th>{TABLE_HEADERS[4]}</th>
                <th>
                  <span className="sr-only">Действия</span>
                </th>
              </tr>
            </thead>
            <tbody className={styles.rows}>
              {rows.map((row) => {
                const notes = hasNotes(row)
                return (
                  <Fragment key={row.id}>
                    <tr
                      className={cx(tableClass.clickable, notes ? styles.withNotes : '')}
                      tabIndex={0}
                      aria-label={`${row.userEmail}: ${money(row.creditedAmount)} ${row.assetSymbol}, открыть`}
                      onClick={() => open(row)}
                      onKeyDown={(event) => {
                        if (event.target === event.currentTarget && (event.key === 'Enter' || event.key === ' ')) {
                          event.preventDefault()
                          open(row)
                        }
                      }}
                    >
                      <td>
                        <div className={styles.person}>
                          <Avatar name={row.userEmail} size={28} className={styles.rowAvatar} />
                          <div className={styles.personText}>
                            <div className={styles.email} title={row.userEmail}>
                              {row.userEmail}
                            </div>
                            <div className={tableClass.sub} title={`Зачислен ${when(row.creditedAt)}`}>
                              {when(row.vanishedAt)} · зачислен {creditedShort(row)}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td>
                        <div className={styles.asset}>{row.assetSymbol}</div>
                        <div className={styles.route}>
                          <Net code={row.networkCode} />
                        </div>
                      </td>
                      <td className={`${tableClass.right} num`}>
                        <span className={styles.amount}>{money(row.creditedAmount, 2)}</span>
                        {row.status === 'applied' && row.reversedAmount !== row.creditedAmount ? (
                          <div className={cx(tableClass.sub, styles.subNum)}>списано {money(row.reversedAmount, 2)}</div>
                        ) : null}
                      </td>
                      <td>
                        <TxLink hash={row.txHash} url={row.explorerUrl} head={6} tail={4} />
                      </td>
                      <td>
                        <StatusPill status={row.status} />
                      </td>
                      <td className={tableClass.actions} onClick={(event) => event.stopPropagation()}>
                        {row.status === 'pending_review' ? (
                          <>
                            <Button size="sm" variant="ghost" className={styles.dangerGhost} onClick={() => open(row, 'apply')}>
                              Сторнировать
                            </Button>
                            <Button size="sm" variant="secondary" onClick={() => open(row, 'dismiss')}>
                              Снять
                            </Button>
                          </>
                        ) : (
                          <Button size="sm" variant="ghost" iconRight={<ChevronRight />} onClick={() => open(row)}>
                            Открыть
                          </Button>
                        )}
                      </td>
                    </tr>
                    {notes ? (
                      <tr className={cx(tableClass.clickable, styles.notesRow)} onClick={() => open(row)}>
                        <td colSpan={6}>
                          <RowNotes row={row} />
                        </td>
                      </tr>
                    ) : null}
                  </Fragment>
                )
              })}
            </tbody>
          </Table>
        )}
        {list.data ? <Pager page={page} pageSize={list.data.pageSize} total={list.data.total} onPage={setPage} /> : null}
      </Panel>

      <Modal
        open={openId !== null}
        onClose={close}
        title={title}
        icon={mode === 'dismiss' ? <ShieldCheck /> : <Undo2 />}
        tone={modalTone}
        subtitle={r === null ? undefined : `${r.userEmail} · ${money(r.creditedAmount)} ${r.assetSymbol} · ${r.networkCode} · ${short(r.txHash, 10, 8)}`}
        wide={mode === 'view'}
        footer={
          r === null ? null : mode === 'view' ? (
            <>
              <Button variant="ghost" onClick={close}>
                Закрыть
              </Button>
              {canDecide ? (
                <>
                  <Button variant="secondary" onClick={() => setMode('dismiss')}>
                    Снять пересмотр
                  </Button>
                  <Button variant="danger" icon={<Undo2 />} onClick={() => setMode('apply')}>
                    Сторнировать
                  </Button>
                </>
              ) : null}
            </>
          ) : (
            <>
              <Button variant="ghost" onClick={() => setMode('view')}>
                Назад
              </Button>
              <Button
                variant={mode === 'apply' ? 'danger-solid' : 'primary'}
                loading={decide.isPending}
                disabled={!canDecide || reason.trim().length < 3}
                onClick={() => run(mode, r.id)}
              >
                {mode === 'apply'
                  ? projection && !projection.sufficient
                    ? `Списать ${money(projection.wouldReverse)} и записать долг`
                    : 'Списать и сторнировать'
                  : 'Снять: депозит в порядке'}
              </Button>
            </>
          )
        }
      >
        {detail.isError ? <Empty tone="error">{describeError(detail.error)}</Empty> : null}
        {r === null ? (
          detail.isPending && openId !== null ? (
            <div className={styles.loading} aria-busy="true">
              <SkeletonText lines={6} />
            </div>
          ) : null
        ) : mode === 'view' ? (
          <>
            <KeyValue
              labelWidth={176}
              items={[
                {
                  label: 'Статус',
                  value: (
                    <span className={styles.statusLine}>
                      <StatusPill status={r.status} />
                      {r.status === 'applied' ? (
                        <span className={tableClass.muted}>
                          списано {money(r.reversedAmount)} {r.assetSymbol}
                          {Number(r.shortfall) > 0 ? <span className={styles.noteError}>, недостача {money(r.shortfall)} {r.assetSymbol} — пользователь должен эту сумму</span> : null}
                        </span>
                      ) : null}
                    </span>
                  ),
                },
                {
                  label: 'Пользователь',
                  value: (
                    <span className={styles.userLine}>
                      <Avatar name={r.userEmail} size={20} />
                      <Link to={`/users?search=${encodeURIComponent(r.userEmail)}`}>{r.userEmail}</Link>
                      <IdChip value={r.userId} label="id пользователя" />
                    </span>
                  ),
                },
                {
                  label: 'Депозит',
                  value: (
                    <span className={styles.statusLine}>
                      <span className="num">
                        {money(r.depositAmount)} {r.assetSymbol}
                      </span>
                      <Net code={r.networkCode} />
                      <StatusPill status={r.depositStatus} />
                    </span>
                  ),
                },
                {
                  label: 'Транзакция',
                  value: (
                    <span className={styles.statusLine}>
                      <TxLink hash={r.txHash} url={r.explorerUrl} head={14} tail={10} />
                      {r.blockNumber ? <span className={tableClass.muted}>блок {r.blockNumber}</span> : null}
                    </span>
                  ),
                },
                { label: 'Адрес получения', value: <IdChip value={r.address} full label="адрес" /> },
                r.fromAddress ? { label: 'Отправитель', value: <IdChip value={r.fromAddress} full label="адрес" /> } : null,
                {
                  label: 'Что увидел наблюдатель',
                  value: reasonOf(r.reason),
                  hint: r.missingReceiptCount > 0 ? `сеть не подтвердила транзакцию ${r.missingReceiptCount} раз подряд` : undefined,
                },
                r.reviewedAt
                  ? {
                      label: 'Решение',
                      value: `${r.reviewedByEmail ?? r.reviewedBy ?? '—'} · ${when(r.reviewedAt)}`,
                      hint: r.notes ?? undefined,
                    }
                  : null,
              ]}
            />

            <Block title="Хронология">
              <Timeline
                steps={[
                  { label: 'Обнаружен в сети', at: r.detectedAt },
                  { label: 'Зачислен', at: r.creditedAt },
                  { label: 'Последний раз в сети', at: r.lastSeenAt },
                  { label: 'Исчез из сети', at: r.vanishedAt, tone: 'down' },
                  ...(r.appliedAt ? [{ label: 'Сторнирован', at: r.appliedAt, tone: 'warning' as const }] : []),
                ]}
              />
            </Block>

            <Block title={`Баланс ${r.assetSymbol} сейчас`}>
              <table className={styles.balances}>
                <thead>
                  <tr>
                    <th scope="col">Субсчёт</th>
                    <th scope="col">Доступно</th>
                    <th scope="col">В резерве</th>
                  </tr>
                </thead>
                <tbody>
                  {(detail.data?.balances ?? []).map((balance) => (
                    <tr key={balance.accountType}>
                      <th scope="row">{ACCOUNT_RU[balance.accountType] ?? balance.accountType}</th>
                      <td>{money(balance.available)}</td>
                      <td>{money(balance.locked)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Block>

            {projection ? (
              <Callout tone={projection.sufficient ? 'accent' : 'warning'} title={projection.sufficient ? 'Сторнирование покроет всю сумму' : 'Сторнирование покроет сумму не полностью'}>
                Списывается только доступный остаток на споте: {money(projection.wouldReverse)} {r.assetSymbol} из {money(r.creditedAmount)} {r.assetSymbol}.
                {projection.sufficient
                  ? ' Резерв и другие субсчета не затрагиваются.'
                  : ` Недостающие ${money(projection.wouldShortfall)} ${r.assetSymbol} будут записаны как недостача — в строке сторнирования и в аудите; баланс в минус не уходит, долг автоматически не заводится. Средства на других субсчетах и в резерве не списываются автоматически: если они там есть, используйте ручную корректировку в карточке пользователя.`}
              </Callout>
            ) : null}
            {canDecide && openWithdrawals && openWithdrawals.count > 0 ? (
              <Callout
                tone="warning"
                title={`У пользователя ${openWithdrawals.count} ${openWithdrawals.count === 1 ? 'незавершённый вывод' : 'незавершённых вывода'} ${r.assetSymbol} на ${money(openWithdrawals.amount)}`}
              >
                Их сумма зарезервирована на споте, и сторнирование её не списывает. Отправленный вывод унесёт деньги, которые сторнирование пытается вернуть: сначала решите
                пересмотр, затем — вывод в «Выводах».
              </Callout>
            ) : null}
          </>
        ) : (
          <>
            {mode === 'apply' ? (
              <>
                <Callout tone="down" title="Действие необратимо">
                  Зачисление {money(r.creditedAmount)} {r.assetSymbol} будет отозвано одной проводкой по леджеру, депозит получит статус «отозвано», пользователь — уведомление.
                  Действие необратимо и попадает в аудит.
                </Callout>
                {projection ? (
                  <Callout tone={projection.sufficient ? 'accent' : 'warning'}>
                    {projection.sufficient
                      ? `Доступно на споте ${money(projection.available)} ${r.assetSymbol}: спишется вся сумма.`
                      : `Доступно на споте только ${money(projection.available)} ${r.assetSymbol}: спишется ${money(projection.wouldReverse)}, недостача ${money(projection.wouldShortfall)} ${r.assetSymbol} будет записана на сторнировании и в аудите.`}
                  </Callout>
                ) : null}
                {openWithdrawals && openWithdrawals.count > 0 ? (
                  <Callout tone="down">
                    У пользователя есть незавершённые выводы {r.assetSymbol} на {money(openWithdrawals.amount)} — их резерв это сторнирование не затронет.
                  </Callout>
                ) : null}
              </>
            ) : (
              <Callout tone="info">
                Только если транзакция снова видна в обозревателе или узел отставал. Депозит останется зачисленным, баланс не изменится; решение попадёт в аудит.
              </Callout>
            )}
            <Field label="Причина" hint="Останется в аудите и в строке сторнирования.">
              <Textarea
                value={reason}
                onChange={(event) => setReason(event.target.value)}
                placeholder={mode === 'apply' ? 'Например: реорг подтверждён на двух узлах, транзакции нет ни в одном блоке' : 'Например: узел отставал, транзакция подтверждена в обозревателе'}
              />
            </Field>
          </>
        )}
      </Modal>
    </>
  )
}
